import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ShoppingCart, User, X, Menu } from 'lucide-react';
import { getProducts } from '../services/api';
import { products as mockProducts } from '../data/mock';
import { useCart } from '../context/CartContext';

const Navbar = () => {
  const [shopOpen, setShopOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [products, setProducts] = useState(mockProducts);
  const location = useLocation();
  const { totalItems, setCartOpen } = useCart();
  let closeTimer = null;

  useEffect(() => {
    getProducts()
      .then(setProducts)
      .catch(() => setProducts(mockProducts));
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setShopOpen(false);
    setMobileOpen(false);
  }, [location]);

  const isHome = location.pathname === '/';

  const handleMouseEnter = () => {
    if (closeTimer) clearTimeout(closeTimer);
    setShopOpen(true);
  };
  const handleMouseLeave = () => {
    closeTimer = setTimeout(() => setShopOpen(false), 150);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : isHome ? 'bg-transparent' : 'bg-white shadow-sm'
          }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 z-10 flex items-center -my-4">
            <img
              src="/assets/images/logo.png"
              alt="SELTZ"
              className={`h-24 w-auto object-contain transition-all duration-300 ${scrolled || !isHome ? 'brightness-0' : 'invert brightness-0'}`}
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {/* Shop Seltz Dropdown */}
            <div
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`flex items-center gap-1 font-extrabold text-sm hover:opacity-80 transition-all duration-300 ${scrolled || !isHome ? 'text-[#0a0a0a]' : 'text-white'}`}
                style={{ fontFamily: 'Nunito, sans-serif' }}
              >
                magazin seltz
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${shopOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Dropdown */}
              {shopOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-white rounded-2xl shadow-2xl p-6 w-[calc(100vw-2rem)] max-w-[560px] grid grid-cols-3 gap-4 border border-gray-100">
                  {products.map((product) => (
                    <Link
                      key={product.id}
                      to={`/products/${product.id}`}
                      className="group flex flex-col items-center text-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      <div
                        className="w-full rounded-xl overflow-hidden flex items-center justify-center bg-white border border-gray-50"
                        style={{ height: '140px' }}
                      >
                        <img
                          src={product.image}
                          alt={product.name}
                          className="h-28 w-auto object-contain group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                      <span
                        className="text-[#0a0a0a] font-extrabold text-xs"
                        style={{ fontFamily: 'Nunito, sans-serif' }}
                      >
                        {product.name}
                      </span>
                    </Link>
                  ))}
                  <div className="col-span-3 border-t border-gray-100 pt-3 flex justify-center">
                    <Link
                      to="/shop"
                      className="text-[#0a0a0a] font-black italic text-sm hover:opacity-70 transition-opacity"
                      style={{ fontFamily: 'Nunito, sans-serif' }}
                    >
                      vezi toate produsele →
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Cart icon */}
            <button
              onClick={() => setCartOpen(true)}
              className={`hover:opacity-80 transition-all duration-300 relative ${scrolled || !isHome ? 'text-[#0a0a0a]' : 'text-white'}`}
              aria-label="Cart"
            >
              <ShoppingCart size={20} />
              {totalItems > 0 && (
                <span
                  className={`absolute -top-2 -right-2 text-xs font-black rounded-full w-5 h-5 flex items-center justify-center ${scrolled || !isHome ? 'bg-[#0a0a0a] text-white' : 'bg-white text-[#0a0a0a]'}`}
                  style={{ fontFamily: 'Nunito, sans-serif' }}
                >
                  {totalItems > 9 ? '9+' : totalItems}
                </span>
              )}
            </button>

            <button
              className={`hover:opacity-80 transition-all duration-300 ${scrolled || !isHome ? 'text-[#0a0a0a]' : 'text-white'}`}
              aria-label="Account"
            >
              <User size={20} />
            </button>
          </div>

          {/* Mobile: cart + menu */}
          <div className="flex items-center gap-4 md:hidden">
            <button
              onClick={() => setCartOpen(true)}
              className={`hover:opacity-80 transition-all duration-300 relative ${scrolled || !isHome ? 'text-[#0a0a0a]' : 'text-white'}`}
            >
              <ShoppingCart size={22} />
              {totalItems > 0 && (
                <span className={`absolute -top-2 -right-2 text-xs font-black rounded-full w-5 h-5 flex items-center justify-center ${scrolled || !isHome ? 'bg-[#0a0a0a] text-white' : 'bg-white text-[#0a0a0a]'}`} style={{ fontFamily: 'Nunito, sans-serif' }}>
                  {totalItems}
                </span>
              )}
            </button>
            <button
              className={`z-10 transition-all duration-300 ${scrolled || !isHome ? 'text-[#0a0a0a]' : 'text-white'}`}
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-[#0a0a0a] flex flex-col pt-24 px-6 transition-all duration-300 ${mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
      >
        <div className="flex flex-col gap-6">
          <div>
            <p className="text-white/60 text-xs uppercase tracking-widest mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
              magazin seltz
            </p>
            {products.map((product) => (
              <Link
                key={product.id}
                to={`/products/${product.id}`}
                className="block text-white font-black italic text-2xl py-2 hover:opacity-80 transition-opacity"
                style={{ fontFamily: 'Nunito, sans-serif' }}
              >
                {product.name}
              </Link>
            ))}
            <Link to="/shop" className="block text-white/70 font-bold text-base py-1 hover:opacity-80 transition-opacity" style={{ fontFamily: 'Nunito, sans-serif' }}>
              → toate produsele
            </Link>
          </div>
          <Link to="/about" className="text-white font-black italic text-2xl hover:opacity-80 transition-opacity" style={{ fontFamily: 'Nunito, sans-serif' }}>
            povestea noastră
          </Link>
          <Link to="/contact" className="text-white font-black italic text-2xl hover:opacity-80 transition-opacity" style={{ fontFamily: 'Nunito, sans-serif' }}>
            contact
          </Link>
        </div>
      </div>
    </>
  );
};

export default Navbar;
